/* import __COLOCATED_TEMPLATE__ from './suggestion-card.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { type BlockGroup } from '../shared/suggestion-editor';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import Conversation from 'embercom/objects/inbox/conversation';
import { type StreamResourceManager } from 'embercom/components/reporting/custom/report/drill-in-preview-panel';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type ContentReviewRequest from 'embercom/models/content-service/content-review-request';
import type Store from '@ember-data/store';
import type SuggestionService from 'embercom/services/suggestion-service';
import { CAN_REPORTING_DRILLIN_ACCESS } from 'embercom/lib/knowledge-hub/constants';
import { isEmpty } from '@ember/utils';
import type { LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';

interface Args {
  blockGroup: BlockGroup;
  isPreviousSuggestionButtonDisabled: boolean;
  isNextSuggestionButtonDisabled: boolean;
  onNextSuggestion: (suggestionNumber: number) => void;
  onPreviousSuggestion: (suggestionNumber: number) => void;
  onSuggestionCardClick: () => void;
  isFocused: boolean;
  suggestionNumber: number;
  onSuggestionCardAction: () => void;
  onRejectSuggestion?: () => void;
  isEditorNarrow: boolean;
  activeContent: LocalizedKnowledgeContent;
}

export default class SuggestionCard extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare navbarCollapsingService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare suggestionService: SuggestionService;
  @service declare permissionsService: $TSFixMe;
  @service declare appService: AppService;

  get numberOfPendingSuggestions() {
    return this.suggestionService.numberOfPendingSuggestions;
  }

  @action
  async handleSuggestion(acceptSuggestion: boolean) {
    let { reviewRequest } = this.args.blockGroup;
    if (!reviewRequest) {
      return;
    }
    try {
      await this.permissionsService.checkPermission(
        this.args.activeContent.requiredEditPermissionForKnowledgeHub,
      );
    } catch (e) {
      return;
    }

    try {
      this.removeHighlightFromBlocks();
      reviewRequest.set('status', acceptSuggestion ? Status.Approved : Status.Rejected);
      this.args.onSuggestionCardAction();
      if (acceptSuggestion) {
        this.suggestionService.hasDirtyContent = true;
        // We don't want to save the accepted review request here because we want the edit changes to be persisted only when the user clicks the save button
        return;
      }

      let record: ContentReviewRequest = await this.store.peekRecord(
        'content-service/content-review-request',
        reviewRequest.get('id'),
      );
      await record.save();

      if (this.args.onRejectSuggestion) {
        this.args.onRejectSuggestion();
      }
      this.args.onNextSuggestion(this.args.suggestionNumber);
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.content-editor.review.notification.rejected-edit.error'),
      );
    }
  }

  private removeHighlightFromBlocks() {
    if (this.args.blockGroup.reviewRequest?.get('status') === Status.Pending) {
      let composerApi = this.args.blockGroup.composerApi;
      if (composerApi) {
        composerApi.composer.commands.clearHighlights();
      }
    }
  }

  get reviewRequestCreatedAt() {
    if (this.args.blockGroup.reviewRequest) {
      return this.intl.t('knowledge-hub.content-editor.review.suggestion-card.created-from', {
        date: this.intl.formatRelative(this.args.blockGroup.reviewRequest.get('createdAt'), {
          style: 'short',
        }),
      });
    }
    return '';
  }

  get showCard() {
    return (
      this.args.blockGroup.isEdited ||
      this.args.blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT
    );
  }

  get showEditActionButtons() {
    return this.args.blockGroup.reviewRequest?.get('reviewType') === ReviewType.EDIT_CONTENT;
  }

  get conversationsCount() {
    let suggestion = this.args.blockGroup.reviewRequest?.get('suggestion');
    if (suggestion) {
      return new Set(suggestion.includedConversationIds).size;
    }
    return 0;
  }

  @action
  async openConversations(streamResourceManager: StreamResourceManager) {
    try {
      await this.permissionsService.checkPermission(CAN_REPORTING_DRILLIN_ACCESS);
    } catch (e) {
      return;
    }
    let suggestion = this.args.blockGroup.reviewRequest?.get('suggestion');
    if (suggestion) {
      taskFor(streamResourceManager.showPreviewSideBar).perform(
        suggestion.includedConversationIds[0],
        Conversation,
        suggestion.includedConversationIds,
      );
    }
  }

  @action
  onCloseConversationSideBar() {
    if (this.navbarCollapsingService.collapsed) {
      this.navbarCollapsingService.toggleNavbarCollapsed();
    }
  }

  @action
  goToPrevious(event: MouseEvent) {
    event.stopPropagation();
    if (this.args.isPreviousSuggestionButtonDisabled) {
      return;
    }
    this.args.onPreviousSuggestion(this.args.suggestionNumber);
  }

  @action
  goToNext(event: MouseEvent) {
    event.stopPropagation();
    if (this.args.isNextSuggestionButtonDisabled) {
      return;
    }
    this.args.onNextSuggestion(this.args.suggestionNumber);
  }

  get reviewReason() {
    let reviewReason = this.args.blockGroup.reviewRequest?.get('reviewReason');
    if (isEmpty(reviewReason)) {
      reviewReason = this.intl.t(
        'knowledge-hub.content-editor.review.suggestion-card.review-reason-fallback',
      );
    }

    return reviewReason;
  }

  @action
  handleTextScroll(event: Event) {
    let element = event.target as HTMLElement;
    this.updateMask(element);
  }

  @action
  setupTextOverflow(element: HTMLElement) {
    setTimeout(() => {
      if (element.scrollHeight > element.clientHeight) {
        element.classList.add('has-overflow');
      }
    }, 100);
  }

  //Applies gradient to overflowing text on either top, bottom or both.
  private updateMask(element: HTMLElement) {
    let { scrollTop, scrollHeight, clientHeight } = element;
    let isAtTop = scrollTop === 0;
    let isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
    let textHasOverflow = scrollHeight > clientHeight;

    let maskImage = 'none';

    if (textHasOverflow) {
      if (isAtTop && !isAtBottom) {
        maskImage = 'linear-gradient(180deg, #000 60%, transparent)';
      } else if (isAtBottom && !isAtTop) {
        maskImage = 'linear-gradient(0deg, #000 60%, transparent)';
      } else if (!isAtTop && !isAtBottom) {
        maskImage = 'linear-gradient(180deg, transparent, #000 20%, #000 80%, transparent)';
      }
    }

    element.style.maskImage = maskImage;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::SuggestionCard': typeof SuggestionCard;
    'knowledge-hub/content-editor/content-review/suggestion-card': typeof SuggestionCard;
  }
}
